import type { GpsState, Vehicle } from './types';

// Mint-TMS-Fleet · logic thuần cho tab "Xe & mooc": lọc, đếm GPS, KPI. Không có tiền.

export type FleetFilter = 'all' | 'Truck' | 'Mooc' | Exclude<GpsState, 'na'>;

export const GPS_LABEL: Record<GpsState, string> = { run: 'Đang chạy', idle: 'Đứng chờ', stop: 'Dừng', lost: 'Mất GPS', na: 'Không GPS' };
export const GPS_TONE: Record<GpsState, 'ok' | 'warn' | 'err' | 'none'> = { run: 'ok', idle: 'warn', stop: 'none', lost: 'err', na: 'none' };

/** Hạn đăng kiểm tính là "sắp hết" (board đánh dấu đúng ngày 03/11/2026). */
export const EXPIRING = '03/11/2026';

export const isTruck = (v: Vehicle) => v.kind === 'Truck';

export function filterVehicles(list: readonly Vehicle[], f: FleetFilter, q: string): Vehicle[] {
  const needle = q.trim().toLowerCase();
  return list.filter((v) => {
    const byF = f === 'all' || (f === 'Truck' || f === 'Mooc' ? v.kind === f : v.gps === f);
    if (!byF) return false;
    if (!needle) return true;
    return [v.plate, v.kind === 'Truck' ? 'Đầu kéo' : 'Mooc', v.model, GPS_LABEL[v.gps], v.location, v.tripId ?? v.tripNote, v.inspectionDue, v.odo].join(' ').toLowerCase().includes(needle);
  });
}

export interface GpsSlice { key: Exclude<GpsState, 'na'>; label: string; n: number; pct: number }

/** Đầu kéo theo GPS (donut). */
export function gpsSlices(list: readonly Vehicle[]): GpsSlice[] {
  const trucks = list.filter(isTruck);
  return (['run', 'idle', 'stop', 'lost'] as const).map((key) => {
    const n = trucks.filter((v) => v.gps === key).length;
    return { key, label: GPS_LABEL[key], n, pct: trucks.length ? Math.round((n / trucks.length) * 100) : 0 };
  });
}

export interface FleetKpi { key: string; label: string; value: string; unit: string; tone: '' | 'warn' | 'err' }

export function fleetKpis(list: readonly Vehicle[]): FleetKpi[] {
  const trucks = list.filter(isTruck);
  const mooc = list.length - trucks.length;
  const exp = list.filter((v) => v.inspectionDue === EXPIRING);
  return [
    { key: 'own', label: 'Đầu kéo · mooc', value: `${trucks.length} · ${mooc}`, unit: 'xe nhà', tone: '' },
    { key: 'run', label: 'Đang chạy', value: String(trucks.filter((v) => v.gps === 'run').length), unit: 'GPS trực tiếp', tone: '' },
    { key: 'exp', label: 'Sắp hết hạn giấy tờ', value: String(exp.length), unit: exp.length ? `${exp[0]!.plate} · đăng kiểm 03/11` : '', tone: 'warn' },
    { key: 'lock', label: 'Khoá kỹ thuật', value: String(list.filter((v) => v.locked).length), unit: 'quá hạn bảo dưỡng', tone: 'err' },
  ];
}

/** Dòng chân donut: mooc theo đầu kéo / rỗng ở bãi / lưu kho khách. */
export function moocFoot(list: readonly Vehicle[]): string {
  const mooc = list.filter((v) => !isTruck(v));
  const at = mooc.filter((v) => v.location.startsWith('Theo ')).length;
  const yard = mooc.filter((v) => v.tripNote === 'rỗng').length;
  const locked = list.filter((v) => v.locked).length;
  return `Mooc ${mooc.length}: theo đầu kéo ${at} · rỗng ở bãi ${yard} · lưu kho khách ${mooc.length - at - yard}${locked ? ` · ${locked} đầu kéo đang khoá kỹ thuật` : ''}`;
}

/** Vị trí ghim trên khung bản đồ giữ chỗ (% theo board). */
export const PIN_POS: Readonly<Record<string, readonly [string, string]>> = {
  '50E13970': ['30%', '40%'], '50H75513': ['62%', '62%'], '50H91408': ['52%', '50%'], '50H60625': ['44%', '30%'],
  '50E39407': ['72%', '44%'], '50E32294': ['20%', '22%'], '50E88210': ['64%', '70%'],
};
